import {
  FormSchema,
  InputElementClassUnion,
  isInputClassElement,
  TextInputElement,
} from "@graffer-inc/form-schema";
import { ProcedureDetailType } from "@graffer-inc/cloud-gov-core";
import { InitialMetaInfoMapping } from "./procedures/initial-meta-info-mapping";
import {
  SmartApplyApiError,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";

export type PdfErrorReasons = "render_area_too_small" | "disable_word_break";

export class PdfRenderError {
  public apiError: SmartApplyApiError;
  public elementId?: string;
  public reason?: PdfErrorReasons;

  constructor(public e: SmartApplyApiError) {
    this.apiError = e;
    if (e.rawResponse) {
      const res = e.rawResponse as {
        elementId: string;
        reason: PdfErrorReasons;
      };
      this.elementId = res.elementId;
      this.reason = res.reason;
    }
  }

  message(procDetail: ProcedureDetailType): string | undefined {
    const elemLabelText = this.getLabelText(procDetail);
    if (!elemLabelText) {
      reportUnexpectedError(
        new Error(
          `element label text of [${
            this.elementId
          }] is not found in procedure detail: ${JSON.stringify(procDetail)}`,
        ),
      );
    }
    const elemMsg = elemLabelText ? `項目「${elemLabelText}」` : "ある項目";
    switch (this.reason) {
      case "render_area_too_small":
        return `PDFの生成に失敗しました。${elemMsg}の欄に入力された文字数が多すぎる可能性があります。${elemMsg}の領域の枠を広げるか、文字数を減らして再度お試しください。解決しない場合にはお問い合わせください。
      `;
      case "disable_word_break":
        return `${elemMsg}が、帳票PDFのページ数を超えた位置に割り当てられています。${elemMsg}の割り当てを削除して再度お試しください。解決しない場合にはお問い合わせください。`;
    }
    return undefined;
  }

  private getLabelText(procDetail: ProcedureDetailType): string | undefined {
    const targetElements: InputElementClassUnion[] = [];
    targetElements.push(
      ...FormSchema.fromJSON(procDetail.template.formSchema)
        .flattenDeep()
        .filter(isInputClassElement),
    );
    // 将来的に項目定義と出力設定が分かれることを想定して
    // procDetail.template.pdfOutputSetting?.metaInfoFormSchemaではなくInitialMetaInfoMappingを使う
    targetElements.push(
      ...FormSchema.fromJSON(InitialMetaInfoMapping)
        .flattenDeep()
        .filter(isInputClassElement),
    );
    if (procDetail.template.tenantProcessResultSetting) {
      targetElements.push(
        ...procDetail.template.tenantProcessResultSetting.inputElements.map(
          (e) => new TextInputElement(e),
        ),
      );
    }
    return targetElements.find((e) => e.id === this.elementId)?.label.text;
  }
}
